import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { offerPath, wildfireStoreOffer } from '../utils/offerNavigation';
import TempStart from './TempStart';
import AppShell from '../components/AppShell';
import '../styles/Shop.css';

// The feed runs to thousands of merchants and the endpoint caps a page at 200, so the page
// loads a batch at a time behind a Load more button.
const PAGE_SIZE = 60;
const SEARCH_DELAY_MS = 250;

function listFrom(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.click)) return payload.click;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload)) return payload;
  return [];
}

function hasMorePages(payload) {
  return !!payload?.pagination?.hasMoreClick;
}

function logoOf(item) {
  return item?.logoUrl || item?.small_logo_url || item?.large_logo_url || item?.logo || '';
}

function subtitleOf(item) {
  if (item?.category_name) return String(item.category_name);
  if (typeof item?.category === 'string') return item.category;
  if (Array.isArray(item?.category_list) && item.category_list.length) return String(item.category_list[0]);
  return '';
}

function haystack(store) {
  return [store.name, store.subtitle, store.raw?.description, store.raw?.tags].filter(Boolean).join(' ').toLowerCase();
}

function normalize(payload) {
  const stores = [];
  const seen = {};
  listFrom(payload).forEach((item) => {
    if (!item || item.is_active === false) return;
    const id = item.id != null ? String(item.id) : item.name || item.merchantName;
    if (!id || seen[id]) return;
    seen[id] = true;
    stores.push({
      id,
      name: item.name || item.merchantName || 'Store',
      logo: logoOf(item),
      subtitle: subtitleOf(item),
      offer: wildfireStoreOffer(item),
      raw: item,
    });
  });
  return stores;
}

function ShopOnlinePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [term, setTerm] = useState('');
  const [stores, setStores] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Pages being fetched right now, so dev StrictMode's double-run of the mount effect does
  // not request page 1 twice.
  const inFlight = useRef(new Set());

  // Typing waits a beat, then the list starts over against the new term.
  useEffect(() => {
    const id = setTimeout(() => setTerm(query.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    setStores([]);
    setPage(0);
    setHasMore(true);
    setFailed(false);
  }, [term]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    if (inFlight.current.has(next)) return;
    inFlight.current.add(next);
    setLoading(true);
    try {
      const payload = await apiService.getWildfireOffers({ page: next, pageSize: PAGE_SIZE, q: term });
      const batch = normalize(payload);
      // A pure updater: React calls it twice in dev StrictMode, so it must not touch anything
      // outside. Stores already listed (a repeated page, say) are skipped against the list itself.
      setStores((current) => {
        const listed = new Set(current.map((store) => store.id));
        return current.concat(batch.filter((store) => !listed.has(store.id)));
      });
      setPage(next);
      setHasMore(hasMorePages(payload));
      setFailed(false);
    } catch (_error) {
      setFailed(true);
      setHasMore(false);
    } finally {
      inFlight.current.delete(next);
      setLoading(false);
    }
  }, [loading, hasMore, page, term]);

  // The first batch of each term loads on its own; the rest wait for the button.
  useEffect(() => {
    if (page === 0 && hasMore && !loading) loadMore();
  }, [page, hasMore, loading, loadMore]);

  // The server may ignore the term, so the loaded rows are matched here as well.
  const needle = term.toLowerCase();
  const visible = needle ? stores.filter((store) => haystack(store).includes(needle)) : stores;
  const openStore = (store) => navigate(offerPath(store.offer), { state: { offer: store.offer } });

  return (
    <div className="shop-all">
      <button type="button" className="shop-all-back" onClick={() => navigate('/shop')}>
        <svg viewBox="0 0 8 14" aria-hidden="true">
          <path d="M6.5 1.5 1.5 7l5 5.5" />
        </svg>
        Shop
      </button>
      <div className="shop-all-head">
        <h1>Shop online</h1>
        <p>Earn points at every store below when you shop through Homecrowd.</p>
      </div>
      <label className="shop-all-search">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" />
          <path d="m10.5 10.5 3 3" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search online shops"
          aria-label="Search online shops"
          autoComplete="off"
        />
      </label>

      {visible.length ? (
        <ul className="shop-all-grid">
          {visible.map((store) => (
            <li key={store.id}>
              <button type="button" onClick={() => openStore(store)}>
                <span className="shop-online-tile">
                  {store.logo ? <img src={store.logo} alt="" /> : null}
                </span>
                <span className="shop-all-name">{store.name}</span>
                {store.subtitle ? <span className="shop-all-sub">{store.subtitle}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {!loading && !visible.length ? (
        <p className="shop-empty">
          {failed ? "Couldn't load stores. Try again in a moment." : term ? `No online shops match “${term}”.` : 'Stores will show up here when offers are available.'}
        </p>
      ) : null}

      {hasMore || loading ? (
        <div className="shop-all-more">
          <button type="button" onClick={loadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ShopOnlineGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return (
    <AppShell title="Shop">
      <ShopOnlinePage />
    </AppShell>
  );
}

export default ShopOnlineGate;
